/**
 * Takeoff Page
 * BuilderBel Construction Project Management System
 *
 * Quantity takeoff management with measurements, assemblies, and workflow
 */

import React, { useState, useCallback } from 'react';
import {
  Ruler,
  Plus,
  ArrowLeft,
  MoreHorizontal,
  CheckCircle,
  Send,
  ShieldCheck,
  FileOutput,
  Trash2,
  Edit,
} from 'lucide-react';
import { ModuleLayout, EmptyState } from '../components/ui';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import TakeoffDrawer from '../components/takeoffs/TakeoffDrawer';
import MeasurementDrawer from '../components/takeoffs/MeasurementDrawer';
// AssemblyDrawer will be used when assembly management is added to the page
// import AssemblyDrawer from '../components/takeoffs/AssemblyDrawer';
import {
  useGetTakeoffsQuery,
  useGetTakeoffQuery,
  useCreateTakeoffMutation,
  useUpdateTakeoffMutation,
  useDeleteTakeoffMutation,
  useGetMeasurementsQuery,
  useCreateMeasurementMutation,
  useDeleteMeasurementMutation,
  useGetTakeoffSummaryQuery,
  useCompleteTakeoffMutation,
  useSubmitTakeoffForReviewMutation,
  useApproveTakeoffMutation,
  useExportToEstimateMutation,
} from '../services/takeoffApi';
import type {
  Takeoff as TakeoffType,
  TakeoffMeasurement,
  TakeoffFilterParams,
  TakeoffStatus,
  CreateTakeoffRequest,
  CreateMeasurementRequest,
} from '../types/takeoff';

type ViewMode = 'list' | 'detail';

// Status badge mapping
const statusConfig: Record<TakeoffStatus, { label: string; className: string }> = {
  in_progress: {
    label: 'In Progress',
    className: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  completed: {
    label: 'Completed',
    className: 'bg-green-50 text-green-700 border-green-200',
  },
  reviewed: {
    label: 'Reviewed',
    className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  approved: {
    label: 'Approved',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
};

const Takeoff: React.FC = () => {
  const projectId = useCurrentProjectId();

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedTakeoffId, setSelectedTakeoffId] = useState<string | null>(null);

  // Filters
  const [filters, setFilters] = useState<TakeoffFilterParams>({});
  const [search, setSearch] = useState('');

  // Drawer states
  const [takeoffDrawerOpen, setTakeoffDrawerOpen] = useState(false);
  const [measurementDrawerOpen, setMeasurementDrawerOpen] = useState(false);
  const [editingTakeoff, setEditingTakeoff] = useState<TakeoffType | null>(null);
  const [editingMeasurement, setEditingMeasurement] = useState<TakeoffMeasurement | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);

  // API Queries
  const {
    data: takeoffsData,
    isLoading: takeoffsLoading,
    refetch: refetchTakeoffs,
  } = useGetTakeoffsQuery(
    { projectId: projectId!, filters: { ...filters, search } },
    { skip: !projectId }
  );

  const { data: takeoffData } = useGetTakeoffQuery(
    { projectId: projectId!, takeoffId: selectedTakeoffId! },
    { skip: !projectId || !selectedTakeoffId }
  );

  const { data: measurementsData } = useGetMeasurementsQuery(
    { projectId: projectId!, takeoffId: selectedTakeoffId! },
    { skip: !projectId || !selectedTakeoffId }
  );

  const { data: summaryData } = useGetTakeoffSummaryQuery(
    { projectId: projectId!, takeoffId: selectedTakeoffId! },
    { skip: !projectId || !selectedTakeoffId }
  );

  // Mutations
  const [createTakeoff, { isLoading: isCreating }] = useCreateTakeoffMutation();
  const [updateTakeoff, { isLoading: isUpdating }] = useUpdateTakeoffMutation();
  const [deleteTakeoff] = useDeleteTakeoffMutation();
  const [createMeasurement, { isLoading: isCreatingMeasurement }] = useCreateMeasurementMutation();
  const [deleteMeasurement] = useDeleteMeasurementMutation();
  const [completeTakeoff] = useCompleteTakeoffMutation();
  const [submitForReview] = useSubmitTakeoffForReviewMutation();
  const [approveTakeoff] = useApproveTakeoffMutation();
  const [exportToEstimate] = useExportToEstimateMutation();

  // Derived state
  const takeoffs = takeoffsData?.data?.takeoffs || [];
  const selectedTakeoff = takeoffData?.data;
  const measurements = measurementsData?.data?.measurements || [];
  const summary = summaryData?.data;

  // Handlers
  const handleCreateTakeoff = useCallback(
    async (data: CreateTakeoffRequest) => {
      if (!projectId) return;
      try {
        if (editingTakeoff) {
          await updateTakeoff({
            projectId,
            takeoffId: editingTakeoff._id,
            data,
          }).unwrap();
        } else {
          await createTakeoff({ projectId, data }).unwrap();
        }
        setTakeoffDrawerOpen(false);
        setEditingTakeoff(null);
      } catch {
        // Error handled by RTK Query
      }
    },
    [projectId, editingTakeoff, createTakeoff, updateTakeoff]
  );

  const handleCreateMeasurement = useCallback(
    async (data: CreateMeasurementRequest) => {
      if (!projectId || !selectedTakeoffId) return;
      try {
        await createMeasurement({
          projectId,
          takeoffId: selectedTakeoffId,
          data,
        }).unwrap();
        setMeasurementDrawerOpen(false);
        setEditingMeasurement(null);
      } catch {
        // Error handled by RTK Query
      }
    },
    [projectId, selectedTakeoffId, createMeasurement]
  );

  const handleDeleteTakeoff = useCallback(
    async (takeoffId: string) => {
      if (!projectId) return;
      try {
        await deleteTakeoff({ projectId, takeoffId }).unwrap();
        if (selectedTakeoffId === takeoffId) {
          setViewMode('list');
          setSelectedTakeoffId(null);
        }
      } catch {
        // Error handled by RTK Query
      }
    },
    [projectId, selectedTakeoffId, deleteTakeoff]
  );

  const handleDeleteMeasurement = useCallback(
    async (measurementId: string) => {
      if (!projectId || !selectedTakeoffId) return;
      try {
        await deleteMeasurement({
          projectId,
          takeoffId: selectedTakeoffId,
          measurementId,
        }).unwrap();
      } catch {
        // Error handled by RTK Query
      }
    },
    [projectId, selectedTakeoffId, deleteMeasurement]
  );

  const handleWorkflowAction = useCallback(
    async (takeoffId: string, action: 'complete' | 'submit' | 'approve') => {
      if (!projectId) return;
      try {
        switch (action) {
          case 'complete':
            await completeTakeoff({ projectId, takeoffId }).unwrap();
            break;
          case 'submit':
            await submitForReview({ projectId, takeoffId }).unwrap();
            break;
          case 'approve':
            await approveTakeoff({ projectId, takeoffId }).unwrap();
            break;
        }
      } catch {
        // Error handled by RTK Query
      }
    },
    [projectId, completeTakeoff, submitForReview, approveTakeoff]
  );

  const handleExport = useCallback(
    async (takeoffId: string) => {
      if (!projectId) return;
      try {
        await exportToEstimate({ projectId, takeoffId }).unwrap();
      } catch {
        // Error handled by RTK Query
      }
    },
    [projectId, exportToEstimate]
  );

  const handleRowClick = useCallback((takeoff: TakeoffType) => {
    setSelectedTakeoffId(takeoff._id);
    setViewMode('detail');
  }, []);

  const handleBackToList = useCallback(() => {
    setViewMode('list');
    setSelectedTakeoffId(null);
  }, []);

  // Status filter options
  const statusFilterOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'approved', label: 'Approved' },
  ];

  // ============================================================================
  // DETAIL VIEW
  // ============================================================================

  if (viewMode === 'detail' && selectedTakeoff) {
    return (
      <div className="space-y-6">
        {/* Back + Title */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToList}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold text-gray-900">
                  {selectedTakeoff.name}
                </h1>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusConfig[selectedTakeoff.status].className}`}
                >
                  {statusConfig[selectedTakeoff.status].label}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                {selectedTakeoff.takeoffNumber}
                {selectedTakeoff.description && ` — ${selectedTakeoff.description}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Workflow Actions */}
            {selectedTakeoff.status === 'in_progress' && (
              <button
                onClick={() =>
                  handleWorkflowAction(selectedTakeoff._id, 'complete')
                }
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Mark Complete
              </button>
            )}
            {selectedTakeoff.status === 'completed' && (
              <button
                onClick={() =>
                  handleWorkflowAction(selectedTakeoff._id, 'submit')
                }
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Send className="w-4 h-4" />
                Submit for Review
              </button>
            )}
            {selectedTakeoff.status === 'reviewed' && (
              <button
                onClick={() =>
                  handleWorkflowAction(selectedTakeoff._id, 'approve')
                }
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
              >
                <ShieldCheck className="w-4 h-4" />
                Approve
              </button>
            )}
            {selectedTakeoff.canExport && (
              <button
                onClick={() => handleExport(selectedTakeoff._id)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FileOutput className="w-4 h-4" />
                Export to Estimate
              </button>
            )}
            {selectedTakeoff.isEditable && (
              <button
                onClick={() => {
                  setEditingMeasurement(null);
                  setMeasurementDrawerOpen(true);
                }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Measurement
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        {summary && (
          <div className="grid grid-cols-4 gap-4">
            {[
              {
                label: 'Linear',
                value: `${selectedTakeoff.totalLinear.toFixed(1)} LF`,
                variant: 'bg-blue-50 text-blue-700',
              },
              {
                label: 'Area',
                value: `${selectedTakeoff.totalArea.toFixed(1)} SF`,
                variant: 'bg-green-50 text-green-700',
              },
              {
                label: 'Count',
                value: `${selectedTakeoff.totalCount} EA`,
                variant: 'bg-purple-50 text-purple-700',
              },
              {
                label: 'Volume',
                value: `${selectedTakeoff.totalVolume.toFixed(1)} CF`,
                variant: 'bg-orange-50 text-orange-700',
              },
            ].map(stat => (
              <div
                key={stat.label}
                className="bg-white rounded-xl border border-gray-200 p-4"
              >
                <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                <p className={`text-lg font-semibold ${stat.variant.split(' ')[1]}`}>
                  {stat.value}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Measurements Table */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">
              Measurements ({measurements.length})
            </h3>
          </div>

          {measurements.length === 0 ? (
            <div className="p-8 text-center">
              <Ruler className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No measurements yet</p>
              {selectedTakeoff.isEditable && (
                <button
                  onClick={() => {
                    setEditingMeasurement(null);
                    setMeasurementDrawerOpen(true);
                  }}
                  className="mt-3 text-sm text-brand-600 hover:text-brand-700"
                >
                  Add your first measurement
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      #
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Type
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Label
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Cost Code
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 px-6 py-3">
                      Raw Value
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 px-6 py-3">
                      Adjusted
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Unit
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Page
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 px-6 py-3">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {measurements.map((m, idx) => (
                    <tr
                      key={m._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-3 text-sm text-gray-500">
                        {idx + 1}
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: m.color }}
                          />
                          <span className="text-sm text-gray-700 capitalize">
                            {m.measurementType}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700">
                        {m.label || '—'}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700">
                        {m.costCode || '—'}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700 text-right">
                        {m.rawValue.toFixed(2)}
                      </td>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">
                        {m.adjustedValue.toFixed(2)}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-500">
                        {m.unit}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-500">
                        {m.pageNumber}
                      </td>
                      <td className="px-6 py-3 text-right">
                        {selectedTakeoff.isEditable && (
                          <button
                            onClick={() => handleDeleteMeasurement(m._id)}
                            className="p-1 text-gray-400 hover:text-red-500"
                            title="Delete measurement"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Cost Code Summary */}
        {summary?.costCodeSummary && summary.costCodeSummary.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">
                Cost Code Summary
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Cost Code
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Description
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 px-6 py-3">
                      Count
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 px-6 py-3">
                      Total (Raw)
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 px-6 py-3">
                      Total (Adjusted)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.costCodeSummary.map(cc => (
                    <tr key={cc._id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-gray-700">
                        {cc.costCode}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-500">
                        {cc.costCodeDescription}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700 text-right">
                        {cc.count}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700 text-right">
                        {cc.totalRaw.toFixed(2)}
                      </td>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">
                        {cc.totalAdjusted.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Measurement Drawer */}
        <MeasurementDrawer
          isOpen={measurementDrawerOpen}
          onClose={() => {
            setMeasurementDrawerOpen(false);
            setEditingMeasurement(null);
          }}
          onSubmit={handleCreateMeasurement}
          isSubmitting={isCreatingMeasurement}
          editingMeasurement={editingMeasurement}
        />
      </div>
    );
  }

  // ============================================================================
  // LIST VIEW
  // ============================================================================

  return (
    <>
      <ModuleLayout
        title="Takeoff"
        description="Quantity takeoffs from project drawings"
        icon={<Ruler className="w-5 h-5" />}
        primaryAction={{
          label: 'New Takeoff',
          onClick: () => {
            setEditingTakeoff(null);
            setTakeoffDrawerOpen(true);
          },
          icon: <Plus className="w-4 h-4" />,
        }}
        searchValue={search}
        onSearchChange={setSearch}
        onRefresh={() => refetchTakeoffs()}
        isLoading={takeoffsLoading}
        stats={[
          {
            title: 'Total',
            value: takeoffsData?.data?.pagination?.total || 0,
          },
          {
            title: 'In Progress',
            value: takeoffs.filter(t => t.status === 'in_progress').length,
            variant: 'info',
          },
          {
            title: 'Completed',
            value: takeoffs.filter(t => t.status === 'completed').length,
            variant: 'success',
          },
          {
            title: 'Approved',
            value: takeoffs.filter(t => t.status === 'approved').length,
            variant: 'brand',
          },
        ]}
        filters={
          <select
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            value={filters.status || ''}
            onChange={e =>
              setFilters({
                ...filters,
                status: (e.target.value || undefined) as TakeoffStatus | undefined,
              })
            }
          >
            {statusFilterOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        }
      >
        {takeoffs.length === 0 && !takeoffsLoading ? (
          <EmptyState
            title="No takeoffs yet"
            description="Create your first quantity takeoff to start measuring from project drawings."
            icon={Ruler}
            action={{
              label: 'New Takeoff',
              onClick: () => {
                setEditingTakeoff(null);
                setTakeoffDrawerOpen(true);
              },
            }}
          />
        ) : (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      #
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Name
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Status
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 px-6 py-3">
                      Measurements
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Progress
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 px-6 py-3">
                      Created
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 px-6 py-3">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {takeoffs.map((takeoff) => (
                    <tr
                      key={takeoff._id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(takeoff)}
                    >
                      <td className="px-6 py-3 text-sm text-gray-500">
                        {takeoff.takeoffNumber}
                      </td>
                      <td className="px-6 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {takeoff.name}
                          </p>
                          {takeoff.description && (
                            <p className="text-xs text-gray-500 truncate max-w-xs">
                              {takeoff.description}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusConfig[takeoff.status].className}`}
                        >
                          {statusConfig[takeoff.status].label}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-700 text-right">
                        {takeoff.totalMeasurements}
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-[100px]">
                            <div
                              className="bg-brand-600 h-1.5 rounded-full"
                              style={{
                                width: `${takeoff.progressPercent || 0}%`,
                              }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">
                            {takeoff.progressPercent || 0}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-500">
                        {new Date(takeoff.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <div className="relative inline-block">
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              setActionMenuOpen(
                                actionMenuOpen === takeoff._id
                                  ? null
                                  : takeoff._id
                              );
                            }}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            <MoreHorizontal className="w-4 h-4 text-gray-400" />
                          </button>

                          {actionMenuOpen === takeoff._id && (
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  setEditingTakeoff(takeoff);
                                  setTakeoffDrawerOpen(true);
                                  setActionMenuOpen(null);
                                }}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </button>

                              {takeoff.status === 'in_progress' && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleWorkflowAction(takeoff._id, 'complete');
                                    setActionMenuOpen(null);
                                  }}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Mark Complete
                                </button>
                              )}

                              {takeoff.status === 'completed' && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleWorkflowAction(takeoff._id, 'submit');
                                    setActionMenuOpen(null);
                                  }}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                                >
                                  <Send className="w-4 h-4" />
                                  Submit for Review
                                </button>
                              )}

                              {takeoff.status === 'reviewed' && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleWorkflowAction(takeoff._id, 'approve');
                                    setActionMenuOpen(null);
                                  }}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-50"
                                >
                                  <ShieldCheck className="w-4 h-4" />
                                  Approve
                                </button>
                              )}

                              {takeoff.canExport && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleExport(takeoff._id);
                                    setActionMenuOpen(null);
                                  }}
                                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <FileOutput className="w-4 h-4" />
                                  Export to Estimate
                                </button>
                              )}

                              <hr className="my-1 border-gray-100" />

                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteTakeoff(takeoff._id);
                                  setActionMenuOpen(null);
                                }}
                                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ModuleLayout>

      {/* Takeoff Drawer */}
      <TakeoffDrawer
        isOpen={takeoffDrawerOpen}
        onClose={() => {
          setTakeoffDrawerOpen(false);
          setEditingTakeoff(null);
        }}
        onSubmit={handleCreateTakeoff}
        isSubmitting={isCreating || isUpdating}
        editingTakeoff={editingTakeoff}
      />
    </>
  );
};

export default Takeoff;
